import React, { useEffect, useRef, useState } from 'react';

import { IPaymentMethod } from 'features/payment/types';
import { useIntl } from 'react-intl';
import { TextInput as RNTextInput } from 'react-native';

import { ServiceModeBar } from 'components/app-header/mobile-header/service-mode-bar';
import { CardModal, CustomScrollViewProps } from 'components/card-modal';
import { CardModalHeader } from 'components/card-modal-header';
import { ServiceMode } from 'generated/graphql-gateway';
import { LogUserErrorSeverity, useLogUserErrorMessage } from 'hooks/log-user-error-message';
import { useCart } from 'hooks/use-cart';
import { useLocalStorageState } from 'hooks/use-local-storage-state';
import { Details } from 'pages/cart/details';
import { FutureOrderTimeCard } from 'pages/cart/future-order-time-card';
import { HeaderPoints } from 'pages/cart/header-points';
import { useCartHasUnavailableOffers } from 'pages/cart/hooks/use-cart-has-unavailable-offers';
import { DeliverDetailsErrors } from 'pages/cart/types';
import YourCart from 'pages/cart/your-cart';
import { DeliveryCancellationPolicy } from 'pages/cart/your-cart/delivery-cancellation-policy';
import { useLoyaltyIncentivesAvailability } from 'pages/loyalty/loyalty-incentives-components/use-loyalty-incentives-availability';
import { useAuthContext } from 'state/auth';
import { useCartContext } from 'state/cart';
import { selectors, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { OrderInjectionFlowVariations } from 'state/launchdarkly/variations';
import { IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { logger } from 'utils/logger';

import { Payment } from './payment';

interface ICartContent {
  checkoutPaymentMethod?: IPaymentMethod | null;
  hasAddOns?: boolean;
}

const CartContent = ({ hasAddOns = false, checkoutPaymentMethod }: ICartContent) => {
  const { cartEntries, calculateCartTotalWithDiscount } = useCart();

  const { serverOrder, shouldSkipPriceOrder } = useCartContext();

  const cartScrollView = useRef<CustomScrollViewProps | undefined>(undefined);
  const { formatMessage } = useIntl();

  const { serviceMode } = useServiceModeContext();

  const { user } = useAuthContext();
  const { orderPhoneNumber } = useOrderContext();

  const isDelivery = serviceMode === ServiceMode.DELIVERY;

  const [deliveryDetailsErrors, setDeliveryDetailsErrors] = useState<DeliverDetailsErrors>({});
  const [areDeliveryDetailsValid, setAreDeliveryDetailsValid] = useState(
    isDelivery ? Boolean(orderPhoneNumber) : true
  );
  const orderInjectFlow = useFlag(LaunchDarklyFlag.ORDER_INJECTION_FLOW);
  const isCartCancelEnabled = useFlag(LaunchDarklyFlag.ENABLE_CART_CANCELLATION_POLICY);
  const isDeliveryCancelEnabled = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY_CANCELLATION_POLICY);

  // Present the Pickup Time Selector if:
  // - Order injection is TIMED_FIRE
  const isOrderTimedFire = orderInjectFlow === OrderInjectionFlowVariations.TIMED_FIRE;

  const phoneNumberInputRef = useRef<RNTextInput | null>(null);

  const filteredOfferErrorList = useAppSelector(state =>
    selectors.loyalty.selectIncentiveErrorsFilteredByCode(
      state,
      IncentiveEvaluationErrorCodes.INVALID_PAYMENT_METHOD
    )
  );
  const rewardErrors = useAppSelector(selectors.loyalty.selectRewardsFeedbackMap);
  const selectedLoyaltyOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const { checkLoyaltyOfferAvailability } = useLoyaltyIncentivesAvailability();
  const isSelectedLoyaltyOfferAvailable =
    !selectedLoyaltyOffer || checkLoyaltyOfferAvailability(selectedLoyaltyOffer);
  const cartHasUnavailableOffers = useCartHasUnavailableOffers();

  const [tempFutureOrderingTime] = useLocalStorageState<string | null>({
    key: StorageKeys.TEMP_FUTURE_ORDERING_TIME,
    defaultReturnValue: null,
  });

  const { isCartTotalNegative = false } = calculateCartTotalWithDiscount();

  const isSelectedOfferOrCartOffersUnavailable =
    !isSelectedLoyaltyOfferAvailable || cartHasUnavailableOffers;

  const offerIsValid = !isSelectedOfferOrCartOffersUnavailable;

  const [hasInvalidDeliveryOptions, setHasInvalidDeliveryOptions] = useState(false);

  const isPaymentDisabled =
    !!filteredOfferErrorList.length ||
    !!Object.values(rewardErrors).length ||
    isSelectedOfferOrCartOffersUnavailable ||
    isCartTotalNegative ||
    hasInvalidDeliveryOptions;

  useLogUserErrorMessage({
    message: 'An item in your offer is not available at this restaurant.',
    severity: LogUserErrorSeverity.Recoverable,
    logWhenTrue: isSelectedOfferOrCartOffersUnavailable,
  });

  useLogUserErrorMessage({
    message: 'Your cart does not meet the minimum price required',
    context: {
      serverOrder,
    },
    severity: LogUserErrorSeverity.Recoverable,
    logWhenTrue: isCartTotalNegative,
  });

  // NOTE: Temporary log to help troubleshoot "Offer Unavailable" issue,
  useEffect(() => {
    if (isSelectedOfferOrCartOffersUnavailable) {
      logger.warn({
        cognitoId: user?.cognitoId,
        cartEntries,
        isSelectedLoyaltyOfferAvailable,
        cartHasUnavailableOffers,
        selectedLoyaltyOffer,
        localStorageAppliedOffers: LocalStorage.getItem(StorageKeys.APPLIED_LOYALTY_OFFERS),
        localStorageAppliedRewards: LocalStorage.getItem(StorageKeys.APPLIED_LOYALTY_REWARDS),
        message: 'Displaying "An item in your offer is not available at this restaurant."',
      });
    }
  }, [
    cartEntries,
    cartHasUnavailableOffers,
    isSelectedLoyaltyOfferAvailable,
    isSelectedOfferOrCartOffersUnavailable,
    selectedLoyaltyOffer,
    user?.cognitoId,
  ]);

  useEffect(() => {
    if (filteredOfferErrorList.length > 0) {
      logger.warn({
        cognitoId: user?.cognitoId,
        cartEntries,
        isSelectedLoyaltyOfferAvailable,
        cartHasUnavailableOffers,
        selectedLoyaltyOffer,
        localStorageAppliedOffers: LocalStorage.getItem(StorageKeys.APPLIED_LOYALTY_OFFERS),
        localStorageAppliedRewards: LocalStorage.getItem(StorageKeys.APPLIED_LOYALTY_REWARDS),
        message: 'There is an Offer with errors in the filteredOfferErrorList array',
      });
    }
  }, [
    cartEntries,
    cartHasUnavailableOffers,
    filteredOfferErrorList.length,
    isSelectedLoyaltyOfferAvailable,
    selectedLoyaltyOffer,
    user?.cognitoId,
  ]);

  return (
    <CardModal
      key="cart"
      _headerOverride={
        <>
          <CardModalHeader
            {...{
              headerText: formatMessage({ id: 'yourOrder' }),
              dismissType: 'back',
              invertHeaderColor: true,
              customHeaderRight: <HeaderPoints />,
              isFullScreen: true,
            }}
          />
          <ServiceModeBar hideTopOrderStatusBar isInCart={true} />
        </>
      }
      scrollViewRef={cartScrollView}
      scrollView
      webAnimate={false}
      standardPadding
      invertHeaderColor
      isFullScreen
      footer={
        <Payment
          disabled={isPaymentDisabled}
          offerIsValid={offerIsValid}
          serverOrder={serverOrder}
          scrollViewRef={cartScrollView}
          areDeliveryDetailsValid={areDeliveryDetailsValid}
          setDeliveryDetailsErrors={setDeliveryDetailsErrors}
          phoneNumberInputRef={phoneNumberInputRef}
          shouldSkipPriceOrder={shouldSkipPriceOrder}
        />
      }
    >
      <Details
        isOrderTimedFire={isOrderTimedFire}
        onDeliveryStatusChange={setHasInvalidDeliveryOptions}
        serverOrder={serverOrder}
        serviceMode={serviceMode}
        user={user}
        setAreDeliveryDetailsValid={setAreDeliveryDetailsValid}
        deliveryDetailsErrors={deliveryDetailsErrors}
        phoneNumberInputRef={phoneNumberInputRef}
      />
      {tempFutureOrderingTime && (
        <FutureOrderTimeCard dateString={tempFutureOrderingTime} showIcon />
      )}
      <YourCart checkoutPaymentMethod={checkoutPaymentMethod} showAddExtrasBtn={hasAddOns} />
      {(isCartCancelEnabled || (isDelivery && isDeliveryCancelEnabled)) && (
        <DeliveryCancellationPolicy />
      )}
    </CardModal>
  );
};

export default React.memo(CartContent);
